import { variableNameFromLabel } from '../../../variable-name.helper';
import {
  defaultBlankDecision,
  entryKey,
  isFlaggedValue,
  matchDecision,
  suggestionOf,
  type BlankDecision,
  type BlankEntry,
} from '../../detection-derivations.helper';
import type { ExistingMatch, NewMatch } from '../../models/ai-template-builder.model';

export type EntryOptionId = 'primary' | 'alternative' | 'new' | 'leave';

export interface EntryOption {
  id: EntryOptionId;
  decision: BlankDecision;
  /** The reading behind the option, `null` for leaving the text as it is. */
  match: ExistingMatch | NewMatch | null;
}

const LEAVE: BlankDecision = { kind: 'leave' };

function newFieldFor(entry: BlankEntry): NewMatch {
  return {
    type: 'new',
    name: variableNameFromLabel(entry.title),
    label: entry.title,
    category: 'others',
    fieldType: 'text',
  };
}

/** Every way the customer can answer one spot: the readings detection gave, a new field, or keeping the text. */
export function entryOptions(entry: BlankEntry): EntryOption[] {
  const { alternative, ...primary } = suggestionOf(entry);
  const readings: EntryOption[] = [{ id: 'primary', decision: matchDecision(primary), match: primary }];

  // The customer names the new field themselves, so a second new reading adds nothing.
  if (alternative && !(alternative.type === 'new' && primary.type === 'new')) {
    readings.push({ id: 'alternative', decision: matchDecision(alternative), match: alternative });
  }

  if (!readings.some(({ match }) => match?.type === 'new')) {
    const newField = newFieldFor(entry);

    readings.push({ id: 'new', decision: matchDecision(newField), match: newField });
  }

  return [...readings, { id: 'leave', decision: LEAVE, match: null }];
}

// A new field stays chosen while the customer renames it.
function isSameAnswer(first: BlankDecision, second: BlankDecision): boolean {
  if (first.kind === 'use_existing' && second.kind === 'use_existing') {
    return first.variable === second.variable;
  }

  return first.kind === second.kind;
}

/** The option that shows as chosen. A flagged value has none until the customer answers it. */
export function selectedOptionId(
  entry: BlankEntry,
  blankDecisions: ReadonlyMap<string, BlankDecision>,
): EntryOptionId | null {
  const decision = blankDecisions.get(entryKey(entry)) ?? (isFlaggedValue(entry) ? null : defaultBlankDecision(entry));

  if (!decision) {
    return null;
  }

  return entryOptions(entry).find((option) => isSameAnswer(option.decision, decision))?.id ?? null;
}
